import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { CollectionStatus } from '@main/types';

export const LOCAL_STORAGE_PREFIX = 'insurely_data_collection';

export type CompanyCollectionStatus =
  | 'not-started'
  | 'should-login'
  | 'connecting'
  | 'collecting'
  | 'finished-successfully'
  | 'finished-unsuccessfully'
  | 'skipped';

export interface CompanyToCollectFrom {
  name: string;
  displayName: string;
  status: CompanyCollectionStatus;
  isTwoFactorLoginMethod: boolean;
  shouldSkipCollection?: boolean;
  collectionId?: string;
  currentCollectionTime?: number;
}

interface CollectionMetaData {
  collectionIds: string[];
  companiesCollectedFrom: string[];
  noInsurancesCollected: number;
  collections: {
    company: string;
    status: CollectionStatus;
  }[];
}

export interface CollectDataContext {
  collectionMetaData: CollectionMetaData;
  updateCollectionMetaData: ({
    collectionId,
    companyKey,
    noInsurancesCollected,
    status,
  }: {
    collectionId: string;
    companyKey: string;
    noInsurancesCollected: number;
    status: CollectionStatus;
  }) => void;
  companiesToCollectFrom: CompanyToCollectFrom[];
  setCompaniesToCollectFrom: (companiesToCollectFrom: CompanyToCollectFrom[]) => void;
  updateCompaniesToCollectFromStatus: (
    companyName: string,
    status: CompanyCollectionStatus,
    collectionId?: string,
  ) => void;
  updateCompaniesToCollectFromTime: (companyName: string, time: number) => void;
  updateCompaniesToCollectFromSkip: (companyName: string, shouldSkipCollection: boolean) => void;
}

const getUpdatedCompaniesToCollectFrom = (
  prev: CompanyToCollectFrom[],
  updatedCompany: Partial<CompanyToCollectFrom>,
) =>
  prev.map((company) => {
    if (company.name === updatedCompany.name) {
      return {
        ...company,
        ...updatedCompany,
      };
    }
    return company;
  });

const CollectDataContext = createContext<CollectDataContext | undefined>(undefined);

export function useCollectDataContext(): CollectDataContext {
  const context = useContext(CollectDataContext);

  if (context === undefined) {
    throw new Error('useCollectData must be used within a CollectDataProvider');
  }

  return context;
}

export const CollectDataProvider = ({ children }: { children: React.ReactNode }) => {
  const [companiesToCollectFrom, setCompaniesToCollectFrom] = useState<CompanyToCollectFrom[]>([]);
  const [collectionMetaData, setCollectionMetaData] = useState<CollectionMetaData>({
    collectionIds: [],
    companiesCollectedFrom: [],
    noInsurancesCollected: 0,
    collections: [],
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(
        `${LOCAL_STORAGE_PREFIX}-companiesToCollectFrom`,
        JSON.stringify(companiesToCollectFrom),
      );

      return () => {
        sessionStorage.setItem(
          `${LOCAL_STORAGE_PREFIX}-companiesToCollectFrom`,
          JSON.stringify(companiesToCollectFrom),
        );
      };
    } catch (e) {
      console.error('Could not set items in localstorage', e);
    }
  }, [companiesToCollectFrom]);

  const updateCollectionMetaData = useCallback(
    ({
      collectionId,
      companyKey,
      status,
      noInsurancesCollected,
    }: {
      collectionId: string;
      companyKey: string;
      status: CollectionStatus;
      noInsurancesCollected: number;
    }) => {
      setCollectionMetaData((prev) => ({
        collectionIds: [...prev.collectionIds, collectionId],
        companiesCollectedFrom: [...new Set([...prev.companiesCollectedFrom, companyKey])],
        noInsurancesCollected: prev.noInsurancesCollected + noInsurancesCollected,
        collections: [...prev.collections, { company: companyKey, status }],
      }));
    },
    [setCollectionMetaData],
  );

  const updateCompaniesToCollectFromStatus = useCallback(
    (companyName: string, status: CompanyCollectionStatus, collectionId?: string) => {
      setCompaniesToCollectFrom((prev) =>
        getUpdatedCompaniesToCollectFrom(prev, { name: companyName, status, collectionId }),
      );
    },
    [setCompaniesToCollectFrom],
  );

  const updateCompaniesToCollectFromSkip = useCallback(
    (companyName: string, shouldSkipCollection: boolean) => {
      setCompaniesToCollectFrom((prev) =>
        getUpdatedCompaniesToCollectFrom(prev, { name: companyName, shouldSkipCollection }),
      );
    },
    [setCompaniesToCollectFrom],
  );

  const updateCompaniesToCollectFromTime = useCallback(
    (companyName: string, time: number) => {
      setCompaniesToCollectFrom((prev) =>
        getUpdatedCompaniesToCollectFrom(prev, { name: companyName, currentCollectionTime: time }),
      );
    },
    [setCompaniesToCollectFrom],
  );

  const value: CollectDataContext = useMemo(
    () => ({
      collectionMetaData,
      updateCollectionMetaData,
      companiesToCollectFrom,
      setCompaniesToCollectFrom,
      updateCompaniesToCollectFromStatus,
      updateCompaniesToCollectFromTime,
      updateCompaniesToCollectFromSkip,
    }),
    [
      collectionMetaData,
      updateCollectionMetaData,
      companiesToCollectFrom,
      updateCompaniesToCollectFromStatus,
      updateCompaniesToCollectFromTime,
      updateCompaniesToCollectFromSkip,
    ],
  );

  return <CollectDataContext.Provider value={value}>{children}</CollectDataContext.Provider>;
};
